<script setup lang="ts">
  import { computed, onMounted, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Select } from "@/components/episto-ui/select";
  import { useCampaignsPivotStore } from "@stores/useCampaignsPivotStore";
  import { useAccountsStore } from "@stores/accounts";
  import { useSurveyOptions } from "@utils/composables/filters/useSurveyOptions";

  // Surveys first (the usual entry point), accounts widen the pool.
  const store = useCampaignsPivotStore();
  const { selectedAccountIds, selectedSurveyIds, selectedSurveys } = storeToRefs(store);

  const accountsStore = useAccountsStore();
  const { availableAccountsOptions } = storeToRefs(accountsStore);

  // The survey picker only ever offers what the chosen accounts contain.
  const { bindings: surveyOptionsBindings, load: loadSurveyOptions } = useSurveyOptions(null, selectedAccountIds);

  const surveyOptions = computed(() => {
    const baseOptions = surveyOptionsBindings.value.options.map((o) => ({ value: String(o.value), label: o.label }));
    const baseIds = new Set(baseOptions.map((o) => o.value));
    const selectedNotInBase = selectedSurveys.value
      .filter((s) => !baseIds.has(String(s.id)))
      .map((s) => ({ value: String(s.id), label: s.name || String(s.id) }));
    return [...selectedNotInBase, ...baseOptions];
  });

  function handleSurveyChange(newIds: unknown) {
    const current = selectedSurveyIds.value.map(String);
    const next = (Array.isArray(newIds) ? newIds : []).map(String);
    const added = next.filter((id) => !current.includes(id));
    const removed = current.filter((id) => !next.includes(id));
    [...added, ...removed].forEach((id) => store.toggleSurvey(Number(id)));
  }

  function handleAccountsChange(newIds: unknown) {
    store.setAccounts((Array.isArray(newIds) ? newIds : []).map(Number));
  }

  onMounted(() => {
    if (!availableAccountsOptions.value.length) accountsStore.fetch();
    loadSurveyOptions(1);
  });
  watch(selectedAccountIds, () => loadSurveyOptions(1));
</script>

<template>
  <!-- The trigger is w-full: the wrappers give it its width. -->
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1.5">
      <p class="text-sm font-medium">Surveys</p>
      <Select
        v-bind="surveyOptionsBindings"
        :model-value="selectedSurveyIds.map(String)"
        :options="surveyOptions"
        multiple
        clearable
        placeholder="Select surveys"
        class="bg-white"
        @update:model-value="handleSurveyChange" />
    </div>
    <div class="flex flex-col gap-1.5">
      <p class="text-sm font-medium">Accounts</p>
      <Select
        :model-value="selectedAccountIds"
        :options="availableAccountsOptions"
        multiple
        filterable
        clearable
        placeholder="Select accounts"
        class="bg-white"
        @update:model-value="handleAccountsChange" />
    </div>
  </div>
</template>
